<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers';
import type { InputGenericSingleSelect, MaterialCalculator, MaterialLocationItemMovementModal } from '#build/components';
import { captureException } from '@sentry/capacitor';
import { ErrorWithBreadcrumb } from '~/errors';

type Destination = 'project' | 'location';
type LocationPage = 'locations' | 'materials';
interface Props {
  locationsIds: string[];
  page: LocationPage;
}
const props = defineProps<Props>();
defineExpose({
  openModal
});
const emit = defineEmits<{
  (e: 'locationItemByLocationId' | 'scan-again'): void;
}>();

const { t, n } = useEkI18n();
const { route } = useRouting();
const { openToast, openConfirmAlert } = useUserMessages();
const { setWorksheetAsActive } = useUseChangeWorksheetStatus();

const purchaseInvoice = usePurchaseInvoiceApi();
const inventory = useInventoryAPI();
const worksheetAPI = useWorksheetAPI();

// Stores
const tokenStore = useTokenStore();
const lastUsedLocation = useLocationStore();
const locationItemStore = useLocationItemByLocationIdStore();
const accountSettingsStore = useAccountSettingsStore();
const locationsStore = useGetLocationByFitterIdStore();
const userConfigurationStore = useUserConfigurationStore();
const worksheetDetailsStore = useWorksheetDetailsStore();
const projectCostCode = useCostCodeStore();
const searchAllItemsStore = useSearchAllItemsStore();
const { _getLocationItemByLocationId, _getLocationItemByLocationIdLocationPage } = storeToRefs(locationItemStore);
const { _allItemsRef } = storeToRefs(searchAllItemsStore);

const projectId = ref<number | null>(route.params.projectId ? Number(route.params.projectId) : null);
const projectReference = ref<string>('');
const projectName = ref<string>('');
const selectedDestination = ref<Destination>('project');
const localItem = ref<MaterialItemApp>();
const pendingSubmit = ref<boolean>(false);
const quantity = ref<number>(1);
const isOpen = ref<boolean>(false);
const searchString = ref<string>('');
const isSearching = ref<boolean>(false);
const projectModalIsOpen = ref<boolean>(false);
const selectedLocation = ref<LocationDTO | null>(null);
const calculatorRef = ref<InstanceType<typeof MaterialCalculator> | null>(null);
const locationSelectModal = ref<null | ComponentExposed<typeof InputGenericSingleSelect<LocationDTO[]>>>(null);
const locationMovementModal = ref<null | InstanceType<typeof MaterialLocationItemMovementModal>>(null);
const showSelectProject = ref<boolean>(false);
const allProjects = ref<WorksheetSearchContainerDTO[]>([]);
const selectedCostCode = ref<ProjectCostCodeChildResponseDTO | null>(null);
const selectedLocationItem = ref<LocationItem | null>(null);
const costCodeModal = useTemplateRef('costCodeModal');

const selectedProjectName = computed(() => {
  if (projectId.value === null) {
    return t('input.selectProject', 1); 
  }

  return `${projectReference.value}: ${projectName.value}`;
});
const searchMessage = computed(() => {
  switch (true) {
    case (searchString.value.length < 3):
      return t('search.minCharacters', { count: 3 });
    case (searchString.value.length >= 3 && allProjects.value && allProjects.value.length === 0 && isSearching.value === false):
      return t('search.noResults', { item: t('ekomplet.project', 1) });
    case (isSearching.value === true):
      return t('search.searching');
    default:
      return '';
  }
});

const locations = computed(() => {
  const currentLocationId = localItem.value?.Location?.ID;

  return (locationsStore.getLocationByFitterId as LocationDTO[] ?? [])
    .filter(l => l.ID !== '' && l.ID !== currentLocationId);
});
const estimatedPrice = computed(() => {
  if (localItem.value === undefined || localItem.value.Price === null) {
    return t('labels.notAvailable'); 
  }

  return `${n(localItem.value.Price * quantity.value, 'currency')} ${t('currency.DKK')}`;
});

const formattedSelectedCostCode = computed(() => {
  return selectedCostCode.value ? `${selectedCostCode.value.Alias}: ${selectedCostCode.value.Name}` : t('input.selectCostCode', 1);
});

const isLocationSelectedButNull = computed(() =>
  selectedDestination.value === 'location' && !selectedLocation.value
);

const isLocationSameAsLocalItem = computed(() =>
  selectedDestination.value === 'location' && selectedLocation.value?.ID === localItem.value?.Location?.ID
);

const isProjectSelectedButNull = computed(() =>
  selectedDestination.value === 'project' && projectId.value === null
);

const useCostCode = computed(() => {
  const flatCostCodes = projectCostCode._projectCostCode?.flatMap(rootCostCode => rootCostCode.CostCodes) ?? [];

  return flatCostCodes.filter(costCode => costCode.CanAddMaterials === true).length > 0;
});

const isQuantityZero = computed(() => quantity.value === 0);

const isPendingSubmit = computed(() => pendingSubmit.value === true);

const submitDisabled = computed(() =>
  isLocationSelectedButNull.value ||
  isLocationSameAsLocalItem.value ||
  isProjectSelectedButNull.value ||
  isQuantityZero.value ||
  isPendingSubmit.value ||
  showQuantityWarning.value
);

const amountText = computed(() => {
  const baseText = t('materials.amount');
  const unitShortName = localItem.value?.Unit?.ShortName;

  return unitShortName ? `${baseText} (${unitShortName.toLowerCase()})` : baseText;
});

onMounted(async () => {
  if (accountSettingsStore._accountSettings === null) {
    await accountSettingsStore.getAccountSettings();
  }
});

const showQuantityWarning = computed(() => {
  return !accountSettingsStore.accountSettings.AllowMaterialMovementBelowZero &&
  (
    (localItem.value !== null && quantity.value > (localItem.value?.ItemAmount ?? 0))
    || !hasSufficientInventoryForNegativeMove.value
  );
});

const hasSufficientInventoryForNegativeMove = computed(() => {
  if (
    selectedDestination.value === 'location' &&
    selectedLocation.value &&
    quantity.value < 0
  ) {

    if (selectedLocationItem.value === null) {
      return false;
    }

    return selectedLocationItem.value?.Amount >= Math.abs(quantity.value);
  }

  return true;
});

watch([selectedLocation, quantity], async ([newLocation, newQuantity], [oldLocation, oldQuantity]) => {
  const locationChanged = !oldLocation || (newLocation && oldLocation && newLocation.ID !== oldLocation.ID);
  const quantityBecameNegative = newQuantity < 0 && (oldQuantity === undefined || oldQuantity >= 0);

  if (
    newLocation &&
    newQuantity < 0 &&
    (locationChanged || quantityBecameNegative)
  ) {
    if (localItem.value?.ItemId === null || localItem.value?.ItemId === undefined) {
      selectedLocationItem.value = null;

      return;
    }

    const result = await inventory.getLocationItemForLocation(
      localItem.value?.ItemId,
      newLocation.ID
    );
    selectedLocationItem.value = result?.Data ?? null;
  } else if (newQuantity >= 0) {
    selectedLocationItem.value = null;
  }
});

async function openModal(item: MaterialItemApp, ProjectIdToNull: boolean = false) {
  localItem.value = item;
  lastUsedLocation.getLocation();
  selectedLocation.value = lastUsedLocation._location ?? locations.value[0];
  quantity.value = (item.ItemAmount !== undefined && !accountSettingsStore.accountSettings.AllowMaterialMovementBelowZero && item.ItemAmount <= 0) ? 0 : 1;

  if (useCostCode.value) {
    await findProjectCostCodeForItem(item);
  }

  isOpen.value = true;
  selectedDestination.value = 'project';

  if (ProjectIdToNull) {
    projectId.value = null;
    showSelectProject.value = true;
  }
}

function setCalculatorFocus(event: { detail: { value: string; }; }) {
  if (event.detail.value === 'first') {
    setTimeout(() => {
      calculatorRef?.value?.setFocus();
    }, 100);
  }
}

async function addToProject() {
  if (localItem.value === undefined || projectId.value === null || localItem.value.Location?.ID === undefined) {
    openToast(t('error.generic'), ToastType.ERROR);

    return; 
  }

  if (useCostCode.value && !selectedCostCode.value?.ID) {
    switch (projectCostCode._validationLevel) {
      case MissingCostCodeValidationLevel.Warning:
      {
        const confirmed = await openConfirmAlert(t('alert.warning'), t('project.warnCostCode'), t('buttons.continue'));

        if (!confirmed) {
          return;
        }
        
        break;
      }
      case MissingCostCodeValidationLevel.Block:
        openToast(t('worksheet.userMessages.costCodeRequired'), ToastType.ERROR);

        return;
    }

  }

  pendingSubmit.value = true;

  try {
    await showLoading(t('loading.adding'));

    const request: AddLocationItemsToProjectRequestDTO = {
      ProjectID: projectId.value,
      Items: [{
        Amount: quantity.value,
        LocationID: localItem.value.Location.ID,
        LocationItemID: localItem.value.RecordId,
        ProjectCostCodeID: selectedCostCode.value?.ID ?? null
      }]
    };

    const response = await purchaseInvoice.addLocationItemsToProject(request);

    if (response?.HasErrors === true) {
      return;
    }

    const isSuccess = response && response.Data.ProblematicItems.length === 0;

    if (isSuccess) {
      const translation = t('toast.addedTo', { item: localItem.value.Name, location: t('materials.project') });
      openToast(`${n(quantity.value, 'decimal')} ${localItem.value.Unit?.ShortName} ${translation}`, ToastType.SUCCESS);
      await Promise.all([
        changeWorksheetStatus()
      ]);
      emit('locationItemByLocationId');
      emit('scan-again');
      isOpen.value = false;
      updateItemAmountInStore();
    } else {
      response?.Data.ProblematicItems.forEach(p => openToast(p.Reason, ToastType.ERROR));
      captureException(new ErrorWithBreadcrumb({ 
        category: BreadCrumbCategoryEnum.Materials, 
        message: `Could not add location item with request: ${request}`,
        userName: userConfigurationStore.config.UserName,
        siteName: userConfigurationStore.config.SiteName 
      }));
    }
  } finally {
    await hideLoading();
    pendingSubmit.value = false;
  }
}

function updateItemAmountInStore() {
  if (_getLocationItemByLocationId.value) {
    const item = _getLocationItemByLocationId.value.find(i => i.RecordId === localItem.value!.RecordId);

    if (item?.ItemAmount) {
      item.ItemAmount -= quantity.value;
    }
  }
    
  if (_getLocationItemByLocationIdLocationPage.value) {
    const item = _getLocationItemByLocationIdLocationPage.value.find(i => i.RecordId === localItem.value!.RecordId);

    if (item?.ItemAmount) {
      item.ItemAmount -= quantity.value;
    }
  }

  if (_allItemsRef.value) {
    const item = _allItemsRef.value.find(i => i.RecordId === localItem.value!.RecordId);

    if (item?.ItemAmount) {
      item.ItemAmount -= quantity.value;
    }
  }

  quantity.value = 1;
}

async function moveItemToLocation() {
  if (!localItem.value || !selectedLocation.value ) {
    openToast(t('error.generic'), ToastType.ERROR);

    return; 
  }

  pendingSubmit.value = true;

  try {
    await showLoading(t('loading.moving'));

    const locationItem = findMatchingLocationItem();

    if (locationItem) {
      const isSuccess = locationItem.Location?.ID !== selectedLocation.value?.ID;

      if (isSuccess) {
        const request: MoveLocationItemRequest = {
          LocationItemId: locationItem.RecordId,
          NewLocationId: selectedLocation.value!.ID,
          Amount: quantity.value
        };
        await Promise.all([
          lastUsedLocation.saveLocation(selectedLocation.value),
          inventory.moveItemsBetweenLocations(request)
        ]);
        const translation = t('toast.movedTo', { item: localItem.value.Name, location: selectedLocation.value.Name });
        openToast(`${n(quantity.value, 'decimal')} ${localItem.value.Unit?.ShortName} ${translation}`, ToastType.SUCCESS);
        emit('locationItemByLocationId');
        emit('scan-again');
        updateItemAmountInStore();
      }
    } else {
      openToast(t('materials.userMessages.sameLocation'), ToastType.ERROR);
      captureException(new ErrorWithBreadcrumb({ 
        category: BreadCrumbCategoryEnum.Materials, 
        message: `Could not move item: ${localItem.value} to location: ${selectedLocation.value}`,
        userName: userConfigurationStore.config.UserName,
        siteName: userConfigurationStore.config.SiteName 
      }));
    }
  } finally {
    await hideLoading();
    isOpen.value = false;
    pendingSubmit.value = false;
  }
}

function selectedProjectId(selectedProject: WorksheetSearchContainerDTO) {
  projectId.value = selectedProject.ProjectID;
  projectReference.value = selectedProject.ProjectReference;
  projectName.value = selectedProject.ProjectName;
  projectModalIsOpen.value = false;
}

function findMatchingLocationItem() {
  const locationItems = props.page === 'locations' ? locationItemStore._getLocationItemByLocationIdLocationPage : locationItemStore._getLocationItemByLocationId;
  const allLocationItems = searchAllItemsStore._allItemsRef;

  if (locationItems) {
    return locationItems.find(locationItem =>
      locationItem.RecordId === localItem.value?.RecordId &&
      locationItem.Location?.ID === localItem.value.Location?.ID
    );
  } else if (allLocationItems) {
    return allLocationItems.find(locationItem =>
      locationItem.RecordId === localItem.value?.RecordId &&
      locationItem.Location?.ID === localItem.value.Location?.ID
    );
  }

  return null;
}

function updateQuantity(newValue: number) {
  quantity.value = newValue;
}

function resetSearchInput() {
  searchString.value = '';
  allProjects.value = [];
}

async function searchProjects() {
  if (searchString.value.length >= 3 && !isSearching.value && userConfigurationStore.userConfiguration) {
    isSearching.value = true;

    const userID = await tokenStore.getUserID();

    const request: SearchWorksheetsRequestDTO = {
      SearchTerm: searchString.value,
      FitterID: userConfigurationStore.userConfiguration.FitterID,
      IncludeAllFitters: true,
      IncludeBlockedWorksheets: true,
      SearchAll: false,
      UserID: userID,
      Page: 1,
      PageSize: 50
    };

    const response = await worksheetAPI.searchWorksheetsGrouped(request);

    if (response?.HasErrors === false) {
      allProjects.value = response.Data;
    }

    isSearching.value = false;
  }
}

async function changeWorksheetStatus() {
  if (showSelectProject.value === true) {
    return;
  }

  setWorksheetAsActive(worksheetDetailsStore._worksheetById?.WorksheetID, userConfigurationStore._userConfiguration?.FitterID);
}

function openProjectModal() {
  projectModalIsOpen.value = true;
}

async function findProjectCostCodeForItem(item: MaterialItemApp) {
  const itemProjectCostCode = await projectCostCode.getProjectCostCodeFromItem(item, Number(route.params.projectId), Number(route.params.worksheetId)) ?? null;

  if (itemProjectCostCode?.CanAddMaterials === true) {
    selectedCostCode.value = itemProjectCostCode;
  } else {
    selectedCostCode.value = null;
  }
}
</script>

<template>
  <IonModal
    id="locationActionModal"
    class="ion-padding custom-ion-modal"
    :is-open="isOpen"
    @did-dismiss="isOpen = false;"
  >
    <IonToolbar>
      <IonTitle class="ion-no-padding">
        <div class="ion-text-wrap ion-padding">
          {{ localItem?.Name }}
          <br>
          <IonNote>
            {{ localItem?.Location?.Name }}
            <div class="cart-line">
              <FontAwesomeIcon :icon="['fas', 'pallet-boxes']" />
              <span>{{ localItem?.ItemAmount }}</span>
            </div>
          </IonNote>
        </div>
      </IonTitle>
      <IonButtons slot="end">
        <IonButton @click="isOpen = false">
          <IonIcon class="fa-light fa-xmark" />
        </IonButton>
      </IonButtons>
    </IonToolbar>
    <IonItem lines="none">
      <IonLabel>
        {{ t('materials.label.moveTo') }}
      </IonLabel>
      <IonRadioGroup v-model="selectedDestination">
        <IonRow>
          <IonCol>
            <IonRadio
              value="project"
              label-placement="end"
              alignment="center"
              justify="start"
            >
              {{ t('ekomplet.project') }}
            </IonRadio>
          </IonCol>
          <IonCol>
            <IonRadio
              value="location"
              label-placement="end"
              alignment="center"
              justify="start"
            >
              {{ t('materials.label.location') }}
            </IonRadio>
          </IonCol>
        </IonRow>
      </IonRadioGroup>
    </IonItem>
    <IonItem
      v-if="accountSettingsStore.accountSettings.UseInventory === true && selectedDestination === 'location'"
      lines="none"
      @click="locationSelectModal?.open()"
    >
      <p slot="start">
        {{ t('ekomplet.location', 1) }}
      </p>
      <p slot="end">
        {{ selectedLocation?.Name ?? t('ekomplet.chooseLocation') }}
      </p>
      
      <IonIcon
        slot="end"
        size="small"
        color="dark"
        class="fa-light fa-chevron-down ion-accordion-toggle-icon toggle-icon "
      />
    </IonItem>
    <IonItem
      v-if="accountSettingsStore.accountSettings.UseInventory === true && selectedDestination === 'project' && showSelectProject === true"
      lines="none"
      @click="openProjectModal"
    >
      <p slot="start">
        {{ t('ekomplet.project', 1) }}
      </p>
      <p slot="end">
        {{ selectedProjectName }}
      </p>
      <IonIcon
        slot="end"
        size="small"
        color="dark"
        class="fa-light fa-chevron-down ion-accordion-toggle-icon toggle-icon "
      />
    </IonItem>
    <IonItem
      v-if="useCostCode"
      lines="none"
      @click="costCodeModal?.open()"
    >
      <p slot="start">
        {{ t('ekomplet.costCode',1) }} 
      </p>
      <div
        slot="end"
        class="cost-code-container"
      >
        <FontAwesomeIcon
          v-if="selectedCostCode"
          :icon="['fas', 'circle']"
          class="fa-lg"
          :style="{ color: selectedCostCode?.Color }"
        />
        <p class="cost-code-text">
          {{ formattedSelectedCostCode }}
        </p>
      </div>
      <IonButton
        v-if="selectedCostCode !== null"
        slot="end"
        fill="clear"
        size="small"
        color="dark"
        class="aligned-button"
        @click.stop="selectedCostCode = null"
      >
        <IonIcon class="fa-light fa-xmark" />
      </IonButton>
      <IonIcon
        v-else
        slot="end"
        color="dark"
        size="small"
        class="fa-light fa-chevron-down  ion-accordion-toggle-icon toggle-icon "
      />
    </IonItem>
    <IonAccordionGroup @ion-change="setCalculatorFocus">
      <IonAccordion value="first">
        <IonItem slot="header">
          <div style="flex: 1">
            <IonLabel>
              {{ amountText }}
            </IonLabel>
            <div
              v-if="showQuantityWarning"
              class="quantity-warning"
            >
              <IonNote color="warning">
                {{ t(
                  'materials.userMessages.notEnoughInLocation',
                  {
                    available: n(quantity < 0 ? selectedLocationItem?.Amount ?? 0 : localItem?.ItemAmount ?? 0, 'decimal'),
                    location: quantity < 0 ? selectedLocation?.Name : localItem?.Location?.Name ?? '',
                    unit: localItem?.Unit?.ShortName ?? ''
                  },
                  Math.sign(localItem?.ItemAmount ?? 0) * Math.ceil(Math.abs(localItem?.ItemAmount ?? 0)) ) 
                }}
              </IonNote>
            </div>
          </div>
          <InputStepper
            v-model:model-value="quantity"
            :allow-negative="true"
            class="mr-20"
            @click.stop="calculatorRef?.resetCalculator();"
          />

          <IonIcon class="aligned-button ion-accordion-toggle-icon toggle-icon no-rotate fa-light fa-calculator fa-fw" />
        </IonItem>
        <div
          slot="content"
          class="ion-padding left-aligned-calculator"
        >
          <MaterialCalculator
            ref="calculatorRef"
            :allow-negative="false"
            @update:value="updateQuantity"
          />
        </div>
      </IonAccordion>
    </IonAccordionGroup>

    <IonItem
      v-if="localItem?.Price !== null"
      lines="none"
    >
      <IonText
        slot="start"
        color="primary"
      >
        <strong>{{ t('materials.label.price') }}</strong>
      </IonText>
      <IonText
        slot="end"
        color="primary"
      >
        <strong>{{ estimatedPrice }}</strong>
      </IonText>
    </IonItem>

    <IonRow class="sm-padding">
      <IonCol>
        <IonButton
          expand="block"
          shape="round"
          fill="outline"
          @click=" locationMovementModal?.open(localItem);
          "
        >
          {{ t('buttons.movements') }}
        </IonButton>
      </IonCol>
      <IonCol>
        <IonButton
          v-if="selectedDestination === 'project'"
          expand="block"
          shape="round"
          :disabled="submitDisabled"
          @click="addToProject()"
        >
          {{ t('buttons.add') }}
        </IonButton>
        <IonButton
          v-else-if="selectedDestination === 'location'"
          expand="block"
          shape="round"
          :disabled="submitDisabled"
          @click="moveItemToLocation()"
        >
          {{ t('buttons.move') }}
        </IonButton>
      </IonCol>
    </IonRow>
  </IonModal>
  <Modal
    v-model:open="projectModalIsOpen"
    :title="t('input.selectProject', 1)"
    class="ion-no-border"
    animation-direction="horizontal"
    :hide-border="true"
    @handle-dismiss="resetSearchInput();"
  >
    <template #subheader>
      <IonToolbar>
        <IonSearchbar
          v-model="searchString"
          :placeholder="t('search.placeholderWorksheet')"
          autocomplete="on"
          :debounce="500"
          @ion-input="searchProjects()"
        />
      </IonToolbar>
    </template>

    <IonContent>
      <span
        v-if="searchMessage.length > 0"
        class="searchMessage"
      >
        {{ searchMessage }}
      </span>
      <IonList v-if="searchString.length >= 3 && !isSearching">
        <IonItem
          v-for="project in allProjects"
          id="listItems"
          slot="content"
          :key="project.ProjectID"
          button
          lines="full"
          :detail="false"
          :value="project.ProjectID"
          @click="selectedProjectId(project);"
        >
          <IonLabel class="ellipsis">
            {{ project.ProjectReference }}: {{ project.ProjectName }}
          </IonLabel>
        </IonItem>
      </IonList>
    </IonContent>
  </Modal>
  <ProjectCostCodeModal
    ref="costCodeModal"
    v-model:model-value="selectedCostCode"
    filter="materials"
    :cost-codes="projectCostCode._projectCostCode ?? []"
  />
  <InputGenericSingleSelect
    ref="locationSelectModal"
    :items="locations"
    :title="t('ekomplet.chooseLocation', 1)"
    :resolve-key="(a: LocationDTO) => a.ID"
    :searchable-fields="['Name']"
    @select="(a: LocationDTO) => selectedLocation = a"
  >
    <template #item="{item}">
      {{ item.Name }}
    </template>
  </InputGenericSingleSelect>
  <MaterialLocationItemMovementModal ref="locationMovementModal" />
</template>

<style scoped>

.custom-ion-modal {
  --height: auto;
  --border-radius: 16px;
}

.sm-padding {
  padding: 8px;
}

.sm-padding-bottom {
  padding-bottom: 8px;
}
.aligned-button {
  margin-right: -6px;
}
.left-aligned-calculator {
  padding-left: 0;
}
.mr-20 {
  margin-right: 15px;
}
.no-rotate {
  transform: none !important;
}
.searchMessage {
      padding: 0.2rem;
      font-size: small;
      color: var(--ion-color-medium);
      display: flex;
      justify-content: center;
}

.cart-line {
  color: var(--ion-color-primary);
  font-weight: bold;
  display: flex;
  gap: 0.3rem;
  padding-top: 0.3rem;
}
.cost-code-container p {
  margin-left: 0.5rem;
}
.cost-code-container {
  display: flex;
  align-items: center;
}

.cost-code-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}
</style>
